import { apiClient } from '../../lib/api-client';
import { PATIENT_UHID_SEARCH_LIMIT } from '../../lib/patient-uhid-search';
import type {
  CreateAppointmentPayload,
  CreateRegistrationPayload,
  OpdAppointment,
  OpdListFilters,
  OpdLookupOption,
  OpdPayment,
  OpdPrescription,
  OpdPrescriptionListRow,
  OpdRegistration,
  OpdSchedule,
  Pagination,
  UpdateRegistrationPayload,
  UpsertOpdPrescriptionPayload,
} from './types';

interface ApiResponse<T> { success: true; data: T }
interface PaginatedResponse<T> extends ApiResponse<T[]> { pagination: Pagination }

const compactParams = (params: object) => Object.fromEntries(
  Object.entries(params).filter(([, value]) => value !== '' && value !== undefined && value !== null),
);

export const fetchOpdRegistrations = async (filters: OpdListFilters) => {
  const response = await apiClient.get<PaginatedResponse<OpdRegistration>>('/opd/registrations', { params: compactParams(filters) });
  return response.data;
};

export const fetchOpdPrescriptions = async (filters: OpdListFilters) => {
  const response = await apiClient.get<PaginatedResponse<OpdPrescriptionListRow>>('/opd/prescriptions', { params: compactParams(filters) });
  return response.data;
};

export const fetchOpdRegistration = async (id: string) => {
  const response = await apiClient.get<ApiResponse<OpdRegistration>>(`/opd/registrations/${id}`);
  return response.data.data;
};

export const createOpdRegistration = async (payload: CreateRegistrationPayload) => {
  const response = await apiClient.post<ApiResponse<OpdRegistration>>('/opd/registrations', payload);
  return response.data.data;
};

export const updateOpdRegistration = async (id: string, payload: UpdateRegistrationPayload) => {
  const response = await apiClient.patch<ApiResponse<OpdRegistration>>(`/opd/registrations/${id}`, payload);
  return response.data.data;
};

export const updateOpdRegistrationPayment = async (id: string, paymentIndex: number, payload: { mode: OpdPayment['mode']; bankName: string | null; amount: number }) => {
  const response = await apiClient.patch<ApiResponse<OpdRegistration>>(`/opd/registrations/${id}/payments/${paymentIndex}`, payload);
  return response.data.data;
};

export const addOpdRegistrationPayment = async (id: string, payload: { mode: OpdPayment['mode']; bankName: string | null; amount: number }) => {
  const response = await apiClient.post<ApiResponse<OpdRegistration>>(`/opd/registrations/${id}/payments`, payload);
  return response.data.data;
};

export const addOpdRegistrationRefund = async (id: string, payload: { mode: OpdPayment['mode']; bankName: string | null; amount: number }) => {
  const response = await apiClient.post<ApiResponse<OpdRegistration>>(`/opd/registrations/${id}/refunds`, payload);
  return response.data.data;
};

export const cancelOpdRegistrationBill = async (id: string) => {
  const response = await apiClient.post<ApiResponse<OpdRegistration>>(`/opd/registrations/${id}/cancel`);
  return response.data.data;
};

export const fetchOpdPrescription = async (registrationId: string) => {
  const response = await apiClient.get<ApiResponse<OpdPrescription>>(`/opd/registrations/${registrationId}/prescription`);
  return response.data.data;
};

export const saveOpdPrescription = async (registrationId: string, payload: UpsertOpdPrescriptionPayload, mode: 'create' | 'edit') => {
  const response = await apiClient.put<ApiResponse<OpdPrescription>>(`/opd/registrations/${registrationId}/prescription`, payload, { params: { mode } });
  return response.data.data;
};

export const fetchOpdAppointments = async (filters: OpdListFilters) => {
  const response = await apiClient.get<PaginatedResponse<OpdAppointment>>('/opd/appointments', { params: compactParams(filters) });
  return response.data;
};

export const fetchOpdAppointment = async (id: string) => {
  const response = await apiClient.get<ApiResponse<OpdAppointment>>(`/opd/appointments/${id}`);
  return response.data.data;
};

export const createOpdAppointment = async (payload: CreateAppointmentPayload) => {
  const response = await apiClient.post<ApiResponse<OpdAppointment>>('/opd/appointments', payload);
  return response.data.data;
};

export const rescheduleOpdAppointment = async (id: string, slot: string) => {
  const response = await apiClient.patch<ApiResponse<OpdAppointment>>(`/opd/appointments/${id}/slot`, { slot });
  return response.data.data;
};

export const cancelOpdAppointment = async (id: string) => {
  await apiClient.patch(`/opd/appointments/${id}/cancel`);
};

export const fetchOpdLookups = async (type: string, search = '', departmentId = '', state = '') => {
  const patientLookup = type === 'patients' || type.startsWith('appointment-patients');
  const response = await apiClient.get<ApiResponse<OpdLookupOption[]>>('/opd/lookups', { params: compactParams({ type, search, departmentId, state, limit: patientLookup ? PATIENT_UHID_SEARCH_LIMIT : 500 }) });
  return response.data.data;
};

export const fetchOpdSchedules = async (doctorId: string, fromDate = '', toDate = '', activeOnly = false) => {
  if (!doctorId) return [];
  const response = await apiClient.get<ApiResponse<OpdSchedule[]>>('/opd/schedules', { params: compactParams({ doctorId, fromDate, toDate, activeOnly: String(activeOnly) }) });
  return response.data.data;
};

export const generateOpdSchedules = async (payload: {
  doctor: string; fromDate: string; toDate: string; days: string[]; fromTime: string; toTime: string; timePerSlot: number; patientPerSlot: number;
}) => {
  const response = await apiClient.post<ApiResponse<{ requested: number; created: number; skipped: number }>>('/opd/schedules', payload);
  return response.data.data;
};

export const updateOpdSchedule = async (id: string, payload: { fromTime: string; toTime: string; patientPerSlot: number }) => {
  const response = await apiClient.patch<ApiResponse<OpdSchedule>>(`/opd/schedules/${id}`, payload);
  return response.data.data;
};

export const updateOpdScheduleStatus = async (ids: string[], status: 'active' | 'inactive') => {
  await apiClient.patch('/opd/schedules/status', { ids, status });
};

export const removeOpdSchedules = async (ids: string[]) => {
  await apiClient.patch('/opd/schedules/remove', { ids });
};
